import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DEFAULTS = {
  app_name: "Eazy Analytics",
  tagline: "Insights, made eazy",
  primary_color: "#2B4EFF",
  accent_color: "#FF6B35",
  logo_url: "",
  theme: "light",
};

const BrandingContext = createContext(null);

function applyBranding(b) {
  const root = document.documentElement;
  if (b.primary_color) root.style.setProperty("--brand-ocean", b.primary_color);
  if (b.accent_color) root.style.setProperty("--brand-mint", b.accent_color);
}
function applyTheme(theme) {
  document.documentElement.setAttribute("data-theme", theme === "dark" ? "dark" : "light");
}

export function BrandingProvider({ children }) {
  const { user } = useAuth();
  const [branding, setBranding] = useState(DEFAULTS);
  const [tenant, setTenant] = useState(null);
  const [theme, setThemeState] = useState(() => localStorage.getItem("bi_theme") || "light");

  const refresh = useCallback(async () => {
    // Load platform-wide universal defaults FIRST so we can layer them under
    // the tenant branding — a tenant that hasn't set logo/primary/accent
    // inherits whatever the super admin ships as the platform default.
    let universal = { theme: null, logo_url: "", primary_color: "", accent_color: "" };
    try {
      const { loadPlatformSettings } = await import("@/hooks/usePlatformSettings");
      const p = await loadPlatformSettings();
      universal = {
        theme: p?.universal_theme || null,
        logo_url: p?.default_logo_url || "",
        primary_color: p?.default_primary_color || "",
        accent_color: p?.default_accent_color || "",
      };
    } catch { /* ignore */ }

    try {
      const { data } = await api.get("/tenants/me");
      const tenantBrand = data.branding || {};
      // Fill any blank tenant field with the universal default.
      const merged = {
        ...DEFAULTS,
        // Universal defaults win over DEFAULTS…
        ...(universal.logo_url ? { logo_url: universal.logo_url } : {}),
        ...(universal.primary_color ? { primary_color: universal.primary_color } : {}),
        ...(universal.accent_color ? { accent_color: universal.accent_color } : {}),
        // …and tenant-specific values win over both.
        ...Object.fromEntries(Object.entries(tenantBrand).filter(([, v]) => v !== "" && v != null)),
      };
      setBranding(merged);
      setTenant(data);
      applyBranding(merged);
      const saved = localStorage.getItem("bi_theme");
      const t = saved || merged.theme || universal.theme || "light";
      setThemeState(t);
      applyTheme(t);
    } catch {
      const fallback = {
        ...DEFAULTS,
        ...(universal.logo_url ? { logo_url: universal.logo_url } : {}),
        ...(universal.primary_color ? { primary_color: universal.primary_color } : {}),
        ...(universal.accent_color ? { accent_color: universal.accent_color } : {}),
      };
      setBranding(fallback);
      applyBranding(fallback);
    }
  }, []);

  useEffect(() => {
    if (user) refresh();
    else applyBranding(DEFAULTS);
  }, [user, refresh]);

  useEffect(() => { applyTheme(theme); }, [theme]);

  const setTheme = (t) => {
    setThemeState(t);
    localStorage.setItem("bi_theme", t);
  };

  const saveBranding = async (tenantId, patch) => {
    const { data } = await api.put(`/tenants/${tenantId}/branding`, patch);
    // Preserve the universal-defaults layer after a save — otherwise clearing
    // a tenant field would wipe the platform fallback until the next refresh.
    let universal = { logo_url: "", primary_color: "", accent_color: "" };
    try {
      const { loadPlatformSettings } = await import("@/hooks/usePlatformSettings");
      const p = await loadPlatformSettings();
      universal = {
        logo_url: p?.default_logo_url || "",
        primary_color: p?.default_primary_color || "",
        accent_color: p?.default_accent_color || "",
      };
    } catch { /* ignore */ }
    const tenantBrand = data.branding || {};
    const merged = {
      ...DEFAULTS,
      ...(universal.logo_url ? { logo_url: universal.logo_url } : {}),
      ...(universal.primary_color ? { primary_color: universal.primary_color } : {}),
      ...(universal.accent_color ? { accent_color: universal.accent_color } : {}),
      ...Object.fromEntries(Object.entries(tenantBrand).filter(([, v]) => v !== "" && v != null)),
    };
    setBranding(merged);
    setTenant(data);
    applyBranding(merged);
    if (patch.theme) setTheme(patch.theme);
    return data;
  };

  return (
    <BrandingContext.Provider value={{ branding, tenant, theme, setTheme, refresh, saveBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = () => useContext(BrandingContext) || { branding: DEFAULTS, theme: "light", setTheme: () => {} };
